import React, { useState, useEffect } from 'react';
import {
    TextField,
    Grid,
    CircularProgress,
    Alert,
    Snackbar,
    Box,
    Switch,
    FormControlLabel,
} from '@mui/material';
import {
    Save as SaveIcon,
    Refresh as RefreshIcon,
    Web as WebIcon,
    SystemUpdate as SystemUpdateIcon,
    Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { PageTitle } from '../../../shared/components/PageTitle';
import { SettingsCard } from '../../../shared/components/SettingsCard';
import { getSettings, updateSiteConfiguration, updateVersionInformation, updateInvoiceConfiguration } from '../../../shared/services/settingService';
import { SiteConfiguration, VersionInformation, InvoiceConfiguration, SettingData } from '../../../shared/types/setting';

export const Settings: React.FC = () => {
    const [setting, setSetting] = useState<SettingData | null>(null);
    const [siteConfig, setSiteConfig] = useState<SiteConfiguration>({
        appName: '',
        supportEmail: '',
        supportPhone: '',
    });
    const [versionInfo, setVersionInfo] = useState<VersionInformation>({
        minimumAppVersion: '',
        latestAppVersion: '',
        updateMessage: '',
        forceUpdate: false,
    });
    const [invoiceConfig, setInvoiceConfig] = useState<InvoiceConfiguration>({
        showCFInvoice: false,
    });
    const [loading, setLoading] = useState(true);
    const [updatingConfig, setUpdatingConfig] = useState(false);
    const [updatingVersion, setUpdatingVersion] = useState(false);
    const [updatingInvoiceConfig, setUpdatingInvoiceConfig] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await getSettings();

            // Handle various response structures from backend
            const data: SettingData | null =
                res?.data?.setting || res?.data?.settings || res?.data?.result || (res as any)?.data || null;

            if (data) {
                setSetting(data);
                if (data.siteConfiguration) {
                    setSiteConfig({
                        appName: data.siteConfiguration.appName || '',
                        supportEmail: data.siteConfiguration.supportEmail || '',
                        supportPhone: data.siteConfiguration.supportPhone || '',
                    });
                }
                if (data.versionInformation) {
                    setVersionInfo({
                        minimumAppVersion: data.versionInformation.minimumAppVersion || '',
                        latestAppVersion: data.versionInformation.latestAppVersion || '',
                        updateMessage: data.versionInformation.updateMessage || '',
                        forceUpdate: data.versionInformation.forceUpdate || false,
                    });
                }
                if (data.invoiceConfiguration) {
                    setInvoiceConfig({
                        showCFInvoice: data.invoiceConfiguration.showCFInvoice ?? false,
                    });
                }
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to fetch settings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleConfigSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setUpdatingConfig(true);
            setError(null);
            const res = await updateSiteConfiguration(siteConfig);
            setSuccess(res.message || 'Site configuration updated successfully');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update site configuration');
        } finally {
            setUpdatingConfig(false);
        }
    };

    const handleVersionSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setUpdatingVersion(true);
            setError(null);
            const res = await updateVersionInformation(versionInfo);
            setSuccess(res.message || 'Version information updated successfully');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update version information');
        } finally {
            setUpdatingVersion(false);
        }
    };

    const handleInvoiceConfigSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setUpdatingInvoiceConfig(true);
            setError(null);
            const res = await updateInvoiceConfiguration(invoiceConfig);
            setSuccess(res.message || 'Invoice configuration updated successfully');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update invoice configuration');
        } finally {
            setUpdatingInvoiceConfig(false);
        }
    };

    const handleCloseSnackbar = () => {
        setError(null);
        setSuccess(null);
    };

    const fieldStyles = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            height: '54px',
            border: '1px solid #e2e8f0',
            '& fieldset': {
                borderColor: 'transparent',
            },
            '&:hover fieldset': {
                borderColor: 'transparent',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'transparent',
                border: '1px solid #6366f1',
            },
        },
        '& .MuiInputBase-input': {
            color: '#1f2937',
            padding: '12px 14px',
            fontSize: '15px',
        },
    };

    const multilineFieldStyles = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            '& fieldset': {
                borderColor: 'transparent',
            },
            '&:hover fieldset': {
                borderColor: 'transparent',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'transparent',
                border: '1px solid #6366f1',
            },
        },
        '& .MuiInputBase-input': {
            color: '#1f2937',
            fontSize: '15px',
        },
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress sx={{ color: '#6366f1' }} />
            </Box>
        );
    }

    return (
        <div className="add-edit-admin-container bg-[#F0F4F8] min-h-[calc(100vh-64px)] py-10 px-4">
            <div className="max-w-5xl mx-auto">
                {/* Header row with title + refresh */}
                <div className="flex items-center justify-between mb-8">
                    <PageTitle title="Settings" />
                    <AppButton
                        variant="secondary"
                        startIcon={<RefreshIcon />}
                        onClick={fetchData}
                        disabled={loading}
                        sx={{
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 600,
                            border: '1px solid #e2e8f0',
                            color: '#486581',
                            backgroundColor: '#fff',
                            '&:hover': { backgroundColor: '#f0f4f8' },
                        }}
                    >
                        Refresh
                    </AppButton>
                </div>

                <div className="space-y-8">
                    {/* --- Site Configuration Card --- */}
                    <SettingsCard
                        title="Site Configuration"
                        subtitle="Manage your application's basic information and support details."
                        icon={<WebIcon sx={{ fontSize: 36, color: '#6366f1' }} />}
                    >
                        <form onSubmit={handleConfigSubmit} className="space-y-6">
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={6}>
                                    <label className="block text-sm font-bold text-[#1f2937] mb-2.5 ml-1">
                                        App Name
                                    </label>
                                    <TextField
                                        fullWidth
                                        required
                                        value={siteConfig.appName}
                                        onChange={(e) =>
                                            setSiteConfig({ ...siteConfig, appName: e.target.value })
                                        }
                                        placeholder="Enter app name"
                                        sx={fieldStyles}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <label className="block text-sm font-bold text-[#1f2937] mb-2.5 ml-1">
                                        Support Email
                                    </label>
                                    <TextField
                                        fullWidth
                                        required
                                        type="email"
                                        value={siteConfig.supportEmail}
                                        onChange={(e) =>
                                            setSiteConfig({ ...siteConfig, supportEmail: e.target.value })
                                        }
                                        placeholder="Enter support email"
                                        sx={fieldStyles}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <label className="block text-sm font-bold text-[#1f2937] mb-2.5 ml-1">
                                        Support Phone
                                    </label>
                                    <TextField
                                        fullWidth
                                        required
                                        value={siteConfig.supportPhone}
                                        onChange={(e) =>
                                            setSiteConfig({ ...siteConfig, supportPhone: e.target.value })
                                        }
                                        placeholder="Enter support phone"
                                        sx={fieldStyles}
                                    />
                                </Grid>
                            </Grid>
                            <div className="flex justify-end mt-6">
                                <AppButton
                                    type="submit"
                                    variant="primary"
                                    startIcon={<SaveIcon />}
                                    loading={updatingConfig}
                                    sx={{
                                        py: 1,
                                        px: 4,
                                        borderRadius: '10px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        backgroundColor: '#6366f1',
                                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                                        '&:hover': { backgroundColor: '#4f46e5' },
                                    }}
                                >
                                    Save Configuration
                                </AppButton>
                            </div>
                        </form>
                    </SettingsCard>

                    {/* --- Version Information Card --- */}
                    <SettingsCard
                        title="Version Information"
                        subtitle="Control app updates, versioning and force update settings."
                        icon={<SystemUpdateIcon sx={{ fontSize: 36, color: '#6366f1' }} />}
                    >
                        <form onSubmit={handleVersionSubmit} className="space-y-6">
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={6}>
                                    <label className="block text-sm font-bold text-[#1f2937] mb-2.5 ml-1">
                                        Minimum App Version
                                    </label>
                                    <TextField
                                        fullWidth
                                        required
                                        value={versionInfo.minimumAppVersion}
                                        onChange={(e) =>
                                            setVersionInfo({ ...versionInfo, minimumAppVersion: e.target.value })
                                        }
                                        placeholder="e.g. 1.0.0"
                                        sx={fieldStyles}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <label className="block text-sm font-bold text-[#1f2937] mb-2.5 ml-1">
                                        Latest App Version
                                    </label>
                                    <TextField
                                        fullWidth
                                        required
                                        value={versionInfo.latestAppVersion}
                                        onChange={(e) =>
                                            setVersionInfo({ ...versionInfo, latestAppVersion: e.target.value })
                                        }
                                        placeholder="e.g. 2.1.0"
                                        sx={fieldStyles}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <label className="block text-sm font-bold text-[#1f2937] mb-2.5 ml-1">
                                        Update Message
                                    </label>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        value={versionInfo.updateMessage}
                                        onChange={(e) =>
                                            setVersionInfo({ ...versionInfo, updateMessage: e.target.value })
                                        }
                                        placeholder="Message to show users when an update is available..."
                                        sx={multilineFieldStyles}
                                    />
                                </Grid>

                            </Grid>
                            <div className="flex justify-end mt-6">
                                <AppButton
                                    type="submit"
                                    variant="primary"
                                    startIcon={<SaveIcon />}
                                    loading={updatingVersion}
                                    sx={{
                                        py: 1,
                                        px: 4,
                                        borderRadius: '10px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        backgroundColor: '#6366f1',
                                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                                        '&:hover': { backgroundColor: '#4f46e5' },
                                    }}
                                >
                                    Save Version Info
                                </AppButton>
                            </div>
                        </form>
                    </SettingsCard>

                    {/* --- Invoice Configuration Card --- */}
                    <SettingsCard
                        title="Invoice Configuration"
                        subtitle="Control invoice display settings for clients."
                        icon={<ReceiptIcon sx={{ fontSize: 36, color: '#6366f1' }} />}
                    >
                        <form onSubmit={handleInvoiceConfigSubmit} className="space-y-6">
                            <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200">
                                <div>
                                    <p className="text-sm font-bold text-[#1f2937]">Show CF Invoice</p>
                                    <p className="text-xs text-gray-500 mt-0.5">Enable or disable CF invoice visibility for clients.</p>
                                </div>
                                <Switch
                                    checked={invoiceConfig.showCFInvoice}
                                    onChange={(e) => setInvoiceConfig({ ...invoiceConfig, showCFInvoice: e.target.checked })}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#6366f1' },
                                    }}
                                />
                            </div>
                            <div className="flex justify-end mt-6">
                                <AppButton
                                    type="submit"
                                    variant="primary"
                                    startIcon={<SaveIcon />}
                                    loading={updatingInvoiceConfig}
                                    sx={{
                                        py: 1,
                                        px: 4,
                                        borderRadius: '10px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        backgroundColor: '#6366f1',
                                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                                        '&:hover': { backgroundColor: '#4f46e5' },
                                    }}
                                >
                                    Save Invoice Config
                                </AppButton>
                            </div>
                        </form>
                    </SettingsCard>
                </div>
            </div>

            {/* Snackbar Notifications */}
            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity="error"
                        variant="filled"
                        sx={{ width: '100%', borderRadius: '12px' }}
                    >
                        {error}
                    </Alert>
                ) : (
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity="success"
                        variant="filled"
                        sx={{ width: '100%', borderRadius: '12px' }}
                    >
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};
